import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SignInForm from '@/components/auth/SignInForm';
import SignUpForm from '@/components/auth/SignUpForm';
import AuthLayout from '@/components/auth/AuthLayout';
import { useAuth } from '@/contexts/auth';
import { useSignin } from '@/hooks/auth/use-signin';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/errors/use-toast";
import { saveLastAuthPage } from '@/utils/localStorage';

type AuthMode = 'signin' | 'signup';

interface AuthPageProps {
  initialMode?: AuthMode;
}

const AuthPage: React.FC<AuthPageProps> = ({ initialMode = 'signin' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { isAuthenticated, isLoading } = useAuth();
  const { signIn } = useSignin();
  
  // Get email from location state if redirected
  const email = location.state?.email || '';
  const showAccountExistsMessage = location.state?.showAccountExistsMessage || false;
  
  useEffect(() => {
    // Update the URL without full navigation
    navigate(`/${mode}`, { replace: true });
    
    // Save the current page to localStorage
    saveLastAuthPage(mode);
  }, [mode, navigate]);

  // Redirect authenticated users to home
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('[AuthPage] User is already authenticated, redirecting to home');
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);
  
  // Handle switching between sign in and sign up
  const handleSwitchMode = (newMode: AuthMode) => {
    setMode(newMode);
  };

  // Handle form submission directly with Supabase
  const handleFormSubmit = async (email: string, password: string, formMode: AuthMode) => {
    try {
      setIsSubmitting(true);
      if (formMode === 'signin') {
        await signIn(email, password);
      } else {
        await handleSignupWithData(email, password);
      }
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom signup handler that accepts email and password
  const handleSignupWithData = async (email: string, password: string) => {

    try {
      setIsSubmitting(true);
      
      // Attempt to sign up the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'user',
          }
        }
      });

      // Check for specific error conditions
      if (error) {
        console.error("Signup error details:", error);
        
        // Handle specific error codes
        if (error.message?.toLowerCase().includes("user already registered") || 
            error.message?.toLowerCase().includes("already in use") ||
            error.message?.toLowerCase().includes("already exists")) {
          // User already exists - show toast and redirect to sign in page
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Redirecting to sign in...",
            variant: "warning",
          });
          
          // Small delay to ensure toast is visible before redirect
          setTimeout(() => {
            navigate('/signin', { 
              state: { 
                email,
                // Don't show additional message since we already showed toast
                showAccountExistsMessage: false
              } 
            });
          }, 1500);
          return false;
        }
        
        // Handle server errors (500)
        if (error.status === 500) {
          toast({
            title: "Registration Error",
            description: "There was a problem with the registration service. Please try again later or contact support if the issue persists.",
            variant: "error",
          });
          return false;
        }
        
        throw error;
      }

      // If identities array is empty, it means the user already exists but hasn't confirmed their email
      if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        console.log("User already exists (empty identities), redirecting to sign in");
        
        // Show toast notification for existing user
        toast({
          title: "Account Already Exists",
          description: "This email is already registered. Redirecting to sign in...",
          variant: "warning",
        });
        
        // Small delay to ensure toast is visible before redirect
        setTimeout(() => {
          navigate('/signin', { 
            state: { 
              email,
              // Don't show additional message since we already showed toast
              showAccountExistsMessage: false
            } 
          });
        }, 1500);
        return false;
      }

      // Navigate to verify email page with success flag and user data
      navigate('/verify-email', { 
        state: { 
          email,
          showSuccessToast: true, // Flag to indicate we just registered successfully
          userData: data?.user ? {
            id: data.user.id,
            email: data.user.email
          } : null
        } 
      });
      return true;

    } catch (error: any) {
      console.error("Registration error:", error);
      
      // Improved error handling with more user-friendly messages
      let errorMessage = "An unexpected error occurred during registration";
      
      if (error.message) {
        if (error.message.toLowerCase().includes("duplicate key") || 
            error.message.toLowerCase().includes("already registered") || 
            error.message.toLowerCase().includes("already in use") ||
            error.message.toLowerCase().includes("already exists")) {
          // User already exists - show toast and redirect to sign in page
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Redirecting to sign in...",
            variant: "warning",
          });
          
          // Small delay to ensure toast is visible before redirect
          setTimeout(() => {
            navigate('/signin', { 
              state: { 
                email,
                // Don't show additional message since we already showed toast
                showAccountExistsMessage: false
              } 
            });
          }, 1500);
          return false;
        } else if (error.message.toLowerCase().includes("password")) {
          errorMessage = "Password error: " + error.message;
        } else if (error.message.toLowerCase().includes("email")) {
          errorMessage = "Email error: " + error.message;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Handle server errors
      if (error.status === 500) {
        errorMessage = "There was a problem with the registration service. Please try again later or contact support if the issue persists.";
      }
      
      toast({
        title: "Registration Error",
        description: errorMessage,
        variant: "error",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  
  // Custom props for each form to handle the smooth transition
  const signInProps = {
    defaultEmail: email,
    isShowingAccountExistsMessage: showAccountExistsMessage,
    onSwitchMode: () => handleSwitchMode('signup'),
    onFormSubmit: async (email: string, password: string) => await handleFormSubmit(email, password, 'signin')
  };
  
  const signUpProps = {
    isLoading: isLoading || isSubmitting,
    onSwitchMode: () => handleSwitchMode('signin'),
    onFormSubmit: async (email: string, password: string) => await handleFormSubmit(email, password, 'signup')
  };
  
  // Title and description based on current mode
  const pageContent = {
    signin: {
      title: "Sign In",
      description: "Welcome back to your private AI portal"
    },
    signup: {
      title: "Create Account",
      description: "Get started with a free account"
    }
  };
  
  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <p className="text-sm text-muted-foreground">
          Checking authentication...
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AuthLayout
        title={pageContent[mode].title}
        description={pageContent[mode].description}
        onSwitchMode={handleSwitchMode}
      >
        <AnimatePresence mode="wait">
          {mode === 'signin' ? (
            <motion.div
              key="signin"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <SignInForm {...signInProps} />
            </motion.div>
          ) : (
            <motion.div
              key="signup"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <SignUpForm {...signUpProps} />
            </motion.div>
          )}
        </AnimatePresence>

      </AuthLayout>
    </motion.div>
  );
};

export default AuthPage;
